import { GRANULARITY } from '@adtrackify/at-tracking-event-types';

import { DEFAULT_COMPARE_PRESET, FILTER_DEBOUNCE_MS } from '../../constants/dashboard-filter-constants';
import { ViewState } from '../../types/dashboard-filter-types';
import { getDefaultViewState } from '../../helpers/get-default-view-state';
import { rememberUnifiedViewId } from '../../helpers/unified-view-session-tracker';
import { isCohortView } from '../../helpers/filters/granularity-helpers';
import { getAccountTimezone } from '@/store/account/timezone-cache';
import * as Logger from '@/lib/monitoring/logger';
import { debounce } from '@/features/dashboard/helpers/debounce';
import {
  getCompareDateRange,
  getDateRangeFromPreset,
  isDateRangeLongerThan,
} from '@/lib/helpers/dates/date-ranges';
import type {
  DateRangeState,
} from '@/lib/types/date-range-types';

import type { DateRangeActions, ImmerSet } from '../../types/filter-actions-types';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function calcCompareRange(
  primaryRange: DateRangeState,
  comparePreset: string | null,
  enableCompare: boolean,
  timezone: string
): DateRangeState | null {
  if (!enableCompare) return null;
  const presetToUse = comparePreset || 'samePeriodLastYear';
  if (presetToUse === 'custom') return null;
  return getCompareDateRange(primaryRange, presetToUse, timezone);
}

export function revalidateGranularityAfterDateChange(
  view: ViewState,
  context: string
): void {
  if (
    view.granularity === GRANULARITY.HOUR &&
    isDateRangeLongerThan(view.dateRange, 1)
  ) {
    Logger.debug('Hourly granularity invalid, defaulting to daily', { context });
    view.granularity = GRANULARITY.DAY;
  }
}

export function autoToggleGranularityAfterDateChange(
  view: ViewState,
  viewId: string,
  context: string
): void {
  if (isCohortView(viewId)) {
    revalidateGranularityAfterDateChange(view, context);
    return;
  }

  const isSingleDay = !isDateRangeLongerThan(view.dateRange, 1);
  const isThreeMonthsOrMore = isDateRangeLongerThan(view.dateRange, 89);
  const isSixMonthsOrMore = isDateRangeLongerThan(view.dateRange, 180);

  const target = isSingleDay
    ? GRANULARITY.HOUR
    : isSixMonthsOrMore
      ? GRANULARITY.MONTH
      : isThreeMonthsOrMore
        ? GRANULARITY.WEEK
        : GRANULARITY.DAY;

  if (view.granularity !== target) {
    Logger.debug(
      'Auto-setting granularity',
      { target, context }
    );
    view.granularity = target;
  }
}

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

export function createDateRangeActions(set: ImmerSet): DateRangeActions {
  const applyDatePreset = debounce((viewId: string, presetName: string) => {
    set(state => {
      const view = state.views[viewId];
      if (!view) return;

      const timezone = getAccountTimezone();
      const defaultUserPreset = getDefaultViewState().preset || 'last30';

      if (!presetName) {
        view.preset = defaultUserPreset;
        view.dateRange = getDateRangeFromPreset(view.preset, timezone, view.includeToday ?? true);
        view.compareDateRange = null;
      } else if (presetName === 'custom') {
        view.preset = 'custom';
        if (view.enableCompareDate) {
          const comparePresetToUse = view.comparePreset || DEFAULT_COMPARE_PRESET;
          if (comparePresetToUse !== 'custom' && view.dateRange) {
            view.compareDateRange = getCompareDateRange(
              view.dateRange,
              comparePresetToUse,
              timezone
            );
          }
        } else {
          view.compareDateRange = null;
        }
      } else {
        const newDateRange = getDateRangeFromPreset(presetName, timezone, view.includeToday ?? true);
        view.preset = presetName;
        view.dateRange = newDateRange;

        view.compareDateRange = calcCompareRange(
          newDateRange,
          view.comparePreset,
          view.enableCompareDate,
          timezone
        );
      }

      autoToggleGranularityAfterDateChange(view, viewId, 'date preset change');
    });
    rememberUnifiedViewId(viewId);
  }, FILTER_DEBOUNCE_MS);

  return {
    recalculateDateRanges: (viewId: string) => {
      set(state => {
        const view = state.views[viewId];
        if (!view) return;

        const timezone = getAccountTimezone();

        if (view.preset && view.preset !== 'custom') {
          view.dateRange = getDateRangeFromPreset(view.preset, timezone, view.includeToday ?? true);
          if (view.enableCompareDate && view.comparePreset && view.comparePreset !== 'custom') {
            view.compareDateRange = getCompareDateRange(
              view.dateRange,
              view.comparePreset,
              timezone
            );
          } else if (view.enableCompareDate && !view.comparePreset) {
            view.compareDateRange = getCompareDateRange(
              view.dateRange,
              'samePeriodLastYear',
              timezone
            );
          }
        } else if (
          view.enableCompareDate &&
          view.comparePreset &&
          view.comparePreset !== 'custom'
        ) {
          view.compareDateRange = getCompareDateRange(
            view.dateRange,
            view.comparePreset,
            timezone
          );
        }
      });
    },

    setDatePreset: (viewId, presetName) => {
      applyDatePreset(viewId, presetName);
    },

    setCustomDateRange: (viewId, range) => {
      set(state => {
        const view = state.views[viewId];
        if (!view) return;

        const timezone = getAccountTimezone();
        const defaultUserPreset = getDefaultViewState().preset || 'last30';

        if (!range?.start || !range?.end) {
          view.preset = defaultUserPreset;
          view.dateRange = getDateRangeFromPreset(view.preset, timezone, view.includeToday ?? true);
          view.compareDateRange = null;
        } else {
          view.dateRange = range;
          view.preset = 'custom';

          view.compareDateRange = calcCompareRange(
            range,
            view.comparePreset,
            view.enableCompareDate,
            timezone
          );
        }

        autoToggleGranularityAfterDateChange(view, viewId, 'custom date range change');
      });
      rememberUnifiedViewId(viewId);
    },

    setIncludeToday: (viewId, includeToday) => {
      set(state => {
        const view = state.views[viewId];
        if (!view) return;

        view.includeToday = includeToday;

        if (view.preset && view.preset !== 'custom') {
          const timezone = getAccountTimezone();
          view.dateRange = getDateRangeFromPreset(view.preset, timezone, includeToday);

          if (view.enableCompareDate && view.comparePreset && view.comparePreset !== 'custom') {
            view.compareDateRange = getCompareDateRange(
              view.dateRange,
              view.comparePreset,
              timezone
            );
          }
        }
      });
      rememberUnifiedViewId(viewId);
    },
  };
}
